import { FC, useState, useEffect } from "react";
import { clientContent } from "../../client/content";
import { withPage } from "../../components/Page";
import { PageHeader } from "../../components/PageHeader";
import { PageToc, TocItem } from "../../components/PageToc";
import { DocumentTable, DocumentRowData } from "../../components/DocumentTable";
import { InfoCallout } from "../../components/InfoCallout";
import styles from "./HealthSafetyElectrical.module.scss";

const TOC_ITEMS: TocItem[] = [
  { id: "section-post-fitout", label: "1. Post-fitout information" },
  { id: "section-hitec", label: "2.1 HITEC UPS" },
  { id: "section-cabling", label: "2.2 IT structured cabling" },
  { id: "section-tclarke", label: "2.3 T Clarke safety standards" },
  { id: "section-permits", label: "2.4 Permits to work" },
  { id: "section-first-aid", label: "2.5 First aid regulations" },
  { id: "section-safe-working", label: "2.6 Safe working practices" },
];

const ROWS_2018: DocumentRowData[] = [
  {
    id: "doc-hse-notification-f10",
    source: "Commerzbank",
    description: "LG Data Centre HSE Notification of Work F10",
    files: [
      {
        name: "LG Data Centre HSE Notification F10.pdf",
        type: "PDF",
        href: "/more-info",
      },
    ],
  },
];

const ROWS_2012: DocumentRowData[] = [
  {
    id: "doc-lv03-method",
    source: "Gmail Upload",
    description:
      "Fixed Wiring Inspection & Test COMMERZBANK, 30 Gresham St. (Switchboard LV03) August 2012 LV03 Method Statement",
    files: [
      {
        name: "LV03 Method Statement (07a. Method Statement(2).doc)",
        type: "DOC",
        href: "/more-info",
      },
    ],
  },
  {
    id: "doc-lv03-risk",
    source: "Gmail Upload",
    description:
      "Fixed Wiring Inspection & Test COMMERZBANK, 30 Gresham St. (Switchboard LV03) August 2012 LV03 Risk Assessment",
    files: [
      {
        name: "LV03 Risk Assessment (07b. Risk Assessment(2).doc)",
        type: "DOC",
        href: "/more-info",
      },
    ],
  },
  {
    id: "doc-lv05-method",
    source: "Gmail Upload",
    description:
      "Fixed Wiring Inspection & Test COMMERZBANK, 30 Gresham St. (Switchboard LV05) August 2012 LV05 Method Statement",
    files: [
      {
        name: "07a. Method Statement.doc",
        type: "DOC",
        href: "/documents/07a. Method Statement.doc",
      },
    ],
  },
  {
    id: "doc-lv05-risk",
    source: "Gmail Upload",
    description:
      "Fixed Wiring Inspection & Test COMMERZBANK, 30 Gresham St. (Switchboard LV05) August 2012 LV05 Risk Assessment",
    files: [
      {
        name: "07b. Risk Assessment.doc",
        type: "DOC",
        href: "/documents/07b. Risk Assessment.doc",
      },
    ],
  },
];

const ROWS_HITEC: DocumentRowData[] = [
  {
    id: "doc-hitec-ups",
    source: "Manual Reference: 887-6",
    description:
      "HITEC Operation & Maintenance Manual Volume 3. Diesel uninterruptible power supply warnings, information recording safe operation, abbreviations and their definitions.",
    files: [
      {
        name: "HITEC UPS H&S Warnings.pdf",
        type: "PDF",
        href: "/more-info",
      },
    ],
  },
];

const ROWS_CABLING: DocumentRowData[] = [
  {
    id: "doc-comms-cabling",
    source: "Manual Ref: 887-12",
    description:
      "Communication IT Structured cabling. Regulations, Acts, BPE.",
    files: [
      {
        name: "Comms IT Cabling H&S.pdf",
        type: "PDF",
        href: "/more-info",
      },
    ],
  },
];

const LEGISLATION_LIST: string[] = [
  "Health and Safety at Work etc. Act 1974",
  "The Factories Act 1961",
  "Offices, Shops and Railway Premises Act 1963",
  "Fire Precautions Act 1971",
  "Lifting Operations and Lifting Equipment Regulations (LOLER) 1998",
  "Asbestos Regulations 1969, 1983, 1985, 1987, 1992",
  "The Control of Pollution Act 1974",
  "Health and Safety (First Aid) Regulations 1981",
  "Reporting of Injuries, Diseases and Dangerous Occurrences Regulations (RIDDOR) 1995",
  "Control of Substances Hazardous to Health Regulations (COSHH) 1988, 1994",
  "Safety Signs & Signals Regulations 1996",
  "The Electricity at Work Regulations 1989",
  "Personal Protective Equipment at Work Regulations 1992",
  "The Management of Health & Safety at Work Regulations 1992",
  "Manual Handling Operations Regulations 1992",
  "Provision & Use of Work Equipment Regulations (PUWER) 1998",
  "Confined Spaces Regulations 1997",
  "Noise at Work Regulations 1989",
];

const HealthSafetyElectrical: FC = () => {
  const [activeSection, setActiveSection] = useState("section-post-fitout");

  useEffect(() => {
    const sectionIds = TOC_ITEMS.map((item) => item.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-20% 0px -70% 0px",
      }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div id="health-safety-electrical" className="page-container-right">
      <PageHeader
        eyebrow="HEALTH & SAFETY / ELECTRICAL"
        title="Health & safety electrical"
        subtitle="Post-fitout notifications, safe systems of work, statutory regulations, and operating standards for electrical installations."
        secondaryAction={{
          label: "Print / export",
          href: "/more-info",
        }}
        primaryAction={{
          label: "Open document library",
          href: "/more-info",
        }}
      />

      <section
        className={styles.introSection}
        aria-label="About this information"
      >
        <div className={styles.aboutCard}>
          <div className={styles.aboutContent}>
            <h2 className={styles.aboutHeading}>About this information</h2>
            <p className={styles.aboutText}>
              This page consolidates health and safety documentation,
              method statements, risk assessments, and statutory operating
              protocols for all electrical infrastructure across the building.
              Source records remain directly accessible in their original file
              format.
            </p>
          </div>
          <div className={styles.statsBar}>
            <div className={styles.statBlock}>
              <span className={styles.statLabel}>Source records</span>
              <span className={styles.statValue}>7 files</span>
            </div>
            <div className={styles.statBlock}>
              <span className={styles.statLabel}>Topics</span>
              <span className={styles.statValue}>7 sections</span>
            </div>
            <div className={styles.statBlock}>
              <span className={styles.statLabel}>Latest source</span>
              <span className={styles.statValue}>2018</span>
            </div>
          </div>
        </div>

        <InfoCallout variant="status" title="Information status">
          Compiled from base-build, fitout contractor submissions and statutory
          guidance. All linked method statements, risk assessments and O&amp;M
          safety manuals are maintained in the public documents repository.
        </InfoCallout>
      </section>

      <div className={styles.layoutGrid}>
        <aside aria-label="Table of contents" className={styles.tocAside}>
          <PageToc
            items={TOC_ITEMS}
            activeId={activeSection}
            onItemClick={(id) => setActiveSection(id)}
          />
        </aside>

        <main className={styles.contentColumn}>
          {/* Section 1: Post Fitout Information */}
          <article
            id="section-post-fitout"
            className={styles.sectionCard}
            aria-labelledby="heading-post-fitout"
          >
            <div className={styles.sectionHeader}>
              <h2 id="heading-post-fitout" className={styles.sectionTitle}>
                1. Post-fitout information
              </h2>
              <p className={styles.sectionIntro}>
                Statutory notifications, method statements, and inspection
                risk assessments grouped by record year.
              </p>
            </div>

            <DocumentTable
              showFilterBar
              filterPlaceholder="Filter files in this section"
              resultCountText="5 documents"
              subheading="2018"
              columnLayout="separateType"
              columns={["SOURCE", "DESCRIPTION", "FILE", "TYPE"]}
              rows={ROWS_2018}
              ariaLabel="2018 Post-fitout electrical records"
            />

            <DocumentTable
              subheading="2012"
              columnLayout="separateType"
              columns={["SOURCE", "DESCRIPTION", "FILE", "TYPE"]}
              rows={ROWS_2012}
              ariaLabel="2012 Post-fitout electrical records"
            />
          </article>

          {/* Section 2.1: HITEC UPS */}
          <article
            id="section-hitec"
            className={styles.sectionCard}
            aria-labelledby="heading-hitec"
          >
            <div className={styles.sectionHeader}>
              <h2 id="heading-hitec" className={styles.sectionTitle}>
                2.1 HITEC UPS
              </h2>
              <p className={styles.sectionIntro}>
                Operation and maintenance safety instructions for diesel
                rotary uninterruptible power supply systems.
              </p>
            </div>

            <DocumentTable
              columnLayout="inlineBadge"
              columns={["SOURCE", "DESCRIPTION", "SUPPORTING FILES"]}
              rows={ROWS_HITEC}
              ariaLabel="HITEC UPS safety documentation"
            />

            <InfoCallout variant="note" title="Operating safety summary">
              Diesel rotary UPS systems involve high-inertia rotating machinery
              and automatic starting sequences. Review all safety warnings,
              interlock checks, and emergency stop locations prior to entering
              the generator plant enclosures.
            </InfoCallout>
          </article>

          {/* Section 2.2: IT Structured Cabling */}
          <article
            id="section-cabling"
            className={styles.sectionCard}
            aria-labelledby="heading-cabling"
          >
            <div className={styles.sectionHeader}>
              <h2 id="heading-cabling" className={styles.sectionTitle}>
                2.2 Communication IT structured cabling
              </h2>
              <p className={styles.sectionIntro}>
                Health and safety guidelines for structured cabling, containment,
                and server room distribution.
              </p>
            </div>

            <DocumentTable
              columnLayout="inlineBadge"
              columns={["SOURCE", "DESCRIPTION", "FILE"]}
              rows={ROWS_CABLING}
              ariaLabel="Communication IT cabling health and safety"
            />
          </article>

          {/* Section 2.3: T Clarke Safety Standards */}
          <article
            id="section-tclarke"
            className={styles.sectionCard}
            aria-labelledby="heading-tclarke"
          >
            <div className={styles.sectionHeader}>
              <h2 id="heading-tclarke" className={styles.sectionTitle}>
                2.3 T Clarke electrical safety standards
              </h2>
              <p className={styles.sectionIntro}>
                Statutory regulations, basic safety hints, fire precautions, and
                tool handling protocols.
              </p>
            </div>

            <h3 className={styles.subheading}>Statutory regulations</h3>
            <p className={styles.bodyParagraph}>
              All parties involved in the operation and maintenance of the
              mechanical and electrical services installation must be aware of and
              act in accordance with the Health and Safety at Work Act 1974, The
              Management of H&amp;S at Work Regulations 1992, the Gas Safety
              (Installation &amp; Use) Regulations 1998, and the current
              regulations for the Electrical Equipment of Buildings (IEE Wiring
              Regulations / BS 7671), together with applicable British Standards
              and Codes of Practice.
            </p>

            <h3 className={styles.subheading}>Safe isolation golden rules</h3>
            <div className={styles.ruleStepsGrid}>
              <div className={styles.ruleStepCard}>
                <span className={styles.ruleStepCode}>Step A</span>
                <span className={styles.ruleStepTitle}>
                  Identify source(s) of supply
                </span>
              </div>
              <div className={styles.ruleStepCard}>
                <span className={styles.ruleStepCode}>Step B</span>
                <span className={styles.ruleStepTitle}>Isolate completely</span>
              </div>
              <div className={styles.ruleStepCard}>
                <span className={styles.ruleStepCode}>Step C</span>
                <span className={styles.ruleStepTitle}>Secure isolation</span>
              </div>
              <div className={styles.ruleStepCard}>
                <span className={styles.ruleStepCode}>Step D</span>
                <span className={styles.ruleStepTitle}>
                  Test dead before working
                </span>
              </div>
            </div>

            <h3 className={styles.subheading}>Basic safety hints</h3>
            <ul className={styles.bulletList}>
              <li>
                Whenever work is to be carried out on motive plant (e.g. fans,
                pumps, etc.) or equipment, the fuses covering the item must be
                removed as an added safeguard.
              </li>
              <li>
                When working alone in remote areas, always inform an Authorised
                person of your exact location and estimated duration.
              </li>
              <li>
                Never attempt a two-person job single-handed; you risk personal
                injury and equipment damage.
              </li>
              <li>
                Observe the &ldquo;one hand&rdquo; rule whenever removing fuses,
                resetting overloads, or operating switches, ensuring your spare
                hand is not contacting earthed metalwork.
              </li>
              <li>
                Always wear mandatory protective clothing and PPE (overalls,
                helmets, eye protection, insulated gloves).
              </li>
              <li>
                When plant is hydraulically isolated for draining, cleaning, or
                inspection, strap or lock off isolating valves.
              </li>
              <li>
                Always verify voltage testing instruments against a known live
                source immediately before and after testing.
              </li>
              <li>
                Before removing fuses, switch off or isolate the circuit serving
                them; never pull a fuse under load.
              </li>
              <li>
                Persons operating or maintaining High Voltage switchgear must
                hold formal certification of competence.
              </li>
              <li>
                <strong>High voltage arc flash warning:</strong> Contact is not
                required for HV flashover—coming within 0.5 inches (12 mm) of an
                uninsulated live HV conductor can cause fatal injury.
              </li>
              <li>
                Always operate a strict <strong>Permit-to-Work</strong> system
                when working on High Voltage switchgear and systems.
              </li>
              <li>
                Never touch two conductors simultaneously, whether presumed live
                or dead.
              </li>
              <li>
                Do not use conductive metal rules or steel measuring tapes near
                live electrical equipment.
              </li>
              <li>
                When pulling fuses, break contact on the incoming side first;
                when replacing, insert into the outgoing side first.
              </li>
              <li>
                Use only tools with insulated grips rated for the maximum working
                voltage.
              </li>
              <li>
                On live work through necessity, ensure a qualified person
                trained in electric shock resuscitation is standing by and
                client permit authorization is in place.
              </li>
              <li>
                Use carbon dioxide (CO<sub>2</sub>) or inert clean gas
                extinguishers on electrical fires—never water, liquid or foam.
              </li>
              <li>
                Attach &ldquo;DANGER&rdquo; and &ldquo;CAUTION&rdquo; notices to
                all live or adjacent apparatus before commencing maintenance.
              </li>
              <li>
                Ensure control circuits and auxiliary transformers back-fed from
                other switchboards are positively disconnected and proven dead.
              </li>
              <li>
                Stand on certified dielectric rubber safety matting when working
                within switchgear cubicles.
              </li>
            </ul>

            <h3 className={styles.subheading}>Fire precautions</h3>
            <ul className={styles.bulletList}>
              <li>
                Regularly verify fusible links on fire valve release lines are
                clean, free of paint, and unobstructed in the airstream.
              </li>
              <li>
                Keep all plant room, electrical riser, and substation access doors
                firmly closed at all times.
              </li>
              <li>
                Stairway fire doors must never be wedged or permanently held open.
              </li>
              <li>
                Maintain hand-operated fire fighting appliances in designated
                brackets with current inspection tags.
              </li>
              <li>
                Obtain a formal Hot Work Permit prior to welding, soldering, or
                torch-applied processes.
              </li>
            </ul>

            <h3 className={styles.subheading}>Tools, scaffolding &amp; ladders</h3>
            <ul className={styles.bulletList}>
              <li>Use tools strictly for their designed purpose; inspect regularly and discard damaged items.</li>
              <li>Never tie ladders together; use appropriate reach equipment.</li>
              <li>Maintain a minimum of three feet (0.9 m) overlap on extension ladders.</li>
              <li>Ensure all ladders have non-slip feet and secure the top against slipping.</li>
              <li>Position ladder bases at the 1:4 ratio (one unit out for every four units of height).</li>
              <li>Keep both hands free when climbing; use tool pouches or hoist lines.</li>
              <li>Barricade the drop zone below elevated work platforms and ladders.</li>
              <li>Use only certified scaffolding erected by competent scaffolders with all wheels locked before mounting.</li>
            </ul>
          </article>

          {/* Section 2.4: Permits to Work */}
          <article
            id="section-permits"
            className={styles.sectionCard}
            aria-labelledby="heading-permits"
          >
            <div className={styles.sectionHeader}>
              <h2 id="heading-permits" className={styles.sectionTitle}>
                2.4 Permits to work
              </h2>
              <p className={styles.sectionIntro}>
                Written safe systems of work for electrical apparatus, high
                voltage equipment, and hazardous operations.
              </p>
            </div>

            <p className={styles.bodyParagraph}>
              Prior to commencing maintenance or modification on electrical
              apparatus, written authorisation must be obtained in the form of a
              <strong>Permit-to-Work</strong>. The permit guarantees that the
              apparatus has been isolated from all sources, proven dead, earthed
              where applicable, and that adjacent live apparatus is locked off
              with warning notices posted.
            </p>

            <div className={styles.permitContainer}>
              <div className={styles.permitTitleBanner}>
                <span className={styles.permitStandard}>
                  Model Procedure · British Standard BS 5405: 1976
                </span>
                <span className={styles.permitMainTitle}>
                  Permit-to-Work on Electrical Apparatus
                </span>
              </div>

              <div className={styles.permitPart}>
                <div className={styles.permitPartHeader}>
                  <span className={styles.permitTag}>Part 1</span>
                  <span>Issue and Making Safe</span>
                </div>
                <div className={styles.permitFieldList}>
                  <div><strong>A. Authorisation:</strong> Designated apparatus and scope of permitted works.</div>
                  <div><strong>B. Switching &amp; Isolation:</strong> Disconnection from all live conductors and locking off.</div>
                  <div><strong>C. Potential Test &amp; Earthing:</strong> Approved voltage indicator test followed by application of portable earth leads.</div>
                  <div><strong>D. Danger Notices:</strong> Posted at boundaries with adjacent live equipment.</div>
                  <div><strong>E. Caution Notices:</strong> Attached at points of isolation and control switches.</div>
                </div>
                <div className={styles.permitFooterData}>
                  <span>Issuer Signature</span>
                  <span>Authorised Rank</span>
                  <span>Time &amp; Date of Issue</span>
                </div>
              </div>

              <div className={styles.permitPart}>
                <div className={styles.permitPartHeader}>
                  <span className={styles.permitTag}>Part 2</span>
                  <span>Declaration of Safety</span>
                </div>
                <p className={styles.bodyParagraph}>
                  Declaration that operations in Parts 1 (B, C, D) are complete,
                  the apparatus is safe to work on, and adjacent live gear is
                  padlocked.
                </p>
                <div className={styles.permitWarningBanner}>
                  ALL OTHER APPARATUS IS DANGEROUS
                </div>
              </div>

              <div className={styles.permitPart}>
                <div className={styles.permitPartHeader}>
                  <span className={styles.permitTag}>Part 3</span>
                  <span>Receipt &amp; Acceptance</span>
                </div>
                <p className={styles.bodyParagraph}>
                  Competent person in charge signs acknowledgement of the
                  permit terms and accepts responsibility that no attempt will be
                  made to work on any other equipment.
                </p>
              </div>

              <div className={styles.permitPart}>
                <div className={styles.permitPartHeader}>
                  <span className={styles.permitTag}>Part 4</span>
                  <span>Clearance of Work</span>
                </div>
                <p className={styles.bodyParagraph}>
                  Certification that work has stopped or been completed, all
                  personnel, gear and tools are withdrawn, and temporary local
                  earths have been removed.
                </p>
              </div>

              <div className={styles.permitPart}>
                <div className={styles.permitPartHeader}>
                  <span className={styles.permitTag}>Part 5</span>
                  <span>Cancellation</span>
                </div>
                <p className={styles.bodyParagraph}>
                  Issuer declares the permit cancelled, earth connections
                  accounted for, and the apparatus ready to be switched back into
                  operational service.
                </p>
              </div>
            </div>
          </article>

          {/* Section 2.5: First Aid */}
          <article
            id="section-first-aid"
            className={styles.sectionCard}
            aria-labelledby="heading-first-aid"
          >
            <div className={styles.sectionHeader}>
              <h2 id="heading-first-aid" className={styles.sectionTitle}>
                2.5 Health and safety (First Aid) regulations 1981
              </h2>
              <p className={styles.sectionIntro}>
                Statutory first-aid guidance for electrical workspaces, priority
                action orders, and specific trauma protocols.
              </p>
            </div>

            <InfoCallout variant="status" title="Rescuer safety priority">
              Take care not to become a casualty yourself while administering
              first aid. Wear protective equipment and ensure circuits are dead
              before contact. If you are not a trained first-aider, summon the
              nearest qualified first-aider immediately or call for medical
              assistance.
            </InfoCallout>

            <h3 className={styles.subheading}>Emergency treatment priorities</h3>
            <div className={styles.prioritiesGrid}>
              <div className={styles.priorityCard}>
                <div className={styles.priorityHeader}>
                  <span className={styles.priorityRank}>1</span>
                  <span className={styles.priorityTitle}>Breathing</span>
                </div>
                <p className={styles.priorityText}>
                  If the casualty has stopped breathing, resuscitation must begin
                  immediately before any other treatment until breathing resumes
                  or paramedics take over.
                </p>
              </div>

              <div className={styles.priorityCard}>
                <div className={styles.priorityHeader}>
                  <span className={styles.priorityRank}>2</span>
                  <span className={styles.priorityTitle}>Severe bleeding</span>
                </div>
                <p className={styles.priorityText}>
                  Apply direct pressure with a sterile dressing or clean pad.
                  Elevate the bleeding limb (unless fractured) to reduce blood flow.
                </p>
              </div>

              <div className={styles.priorityCard}>
                <div className={styles.priorityHeader}>
                  <span className={styles.priorityRank}>3</span>
                  <span className={styles.priorityTitle}>Unconsciousness</span>
                </div>
                <p className={styles.priorityText}>
                  Maintain a clear airway by clearing the mouth and preventing
                  the tongue from obstructing the throat. Place in the recovery
                  position.
                </p>
              </div>

              <div className={styles.priorityCard}>
                <div className={styles.priorityHeader}>
                  <span className={styles.priorityRank}>4</span>
                  <span className={styles.priorityTitle}>Broken bones</span>
                </div>
                <p className={styles.priorityText}>
                  Do not move casualties with suspected fractures or joint injuries
                  unless in immediate danger. Support and immobilise the affected
                  parts first.
                </p>
              </div>
            </div>

            <h3 className={styles.subheading}>Specific treatment protocols</h3>
            <ul className={styles.bulletList}>
              <li>
                <strong>Electric shock:</strong> Ensure the power supply is
                switched off before touching the casualty. If switching off is
                not immediately possible, separate the casualty using certified
                heavy duty insulation gloves (BS 6697/1977) or dry, non-conductive
                implements (rubber, dry timber, or folded paper). Never make bare
                skin-to-skin contact while current flows. Initiate CPR
                immediately if breathing has ceased.
              </li>
              <li>
                <strong>Burns and scalds:</strong> Flush minor burns with clean,
                cool water for at least 10 minutes before dressing. For extensive
                or deep burns, apply a clean dry sterile dressing; never burst
                blisters or remove clothing adhered to burned tissue.
              </li>
              <li>
                <strong>Chemical burns:</strong> Remove contaminated clothing not
                stuck to skin and irrigate with copious clean, cool water to dilute
                the agent. Avoid contaminating yourself during irrigation.
              </li>
              <li>
                <strong>Eye injuries:</strong> Irrigate immediately with clean,
                cool water. For foreign bodies or chemical splashes, cover with a
                sterile eye pad and transfer to hospital without delay.
              </li>
              <li>
                <strong>Gassing &amp; fumes:</strong> Move casualty to fresh air
                wearing suitable respiratory protection. Accompany casualty to
                hospital with the chemical identity and safety data sheet.
              </li>
            </ul>

            <h3 className={styles.subheading}>Statutory recording &amp; kit maintenance</h3>
            <p className={styles.bodyParagraph}>
              All first aid treatments must be recorded in the statutory accident
              book (e.g. BI 510 Social Security Act register). Used materials
              must be replenished immediately to keep first aid stations fully
              stocked.
            </p>
          </article>

          {/* Section 2.6: Safe Working Practice & Bibliography */}
          <article
            id="section-safe-working"
            className={styles.sectionCard}
            aria-labelledby="heading-safe-working"
          >
            <div className={styles.sectionHeader}>
              <h2 id="heading-safe-working" className={styles.sectionTitle}>
                2.6 Safe working practices &amp; statutory bibliography
              </h2>
              <p className={styles.sectionIntro}>
                Hazard control measures for dust, vapours, temporary supplies,
                noise, inspection standards, and statutory Acts.
              </p>
            </div>

            <h3 className={styles.subheading}>Hazard mitigation</h3>
            <ul className={styles.bulletList}>
              <li>
                <strong>Harmful dust &amp; asbestos:</strong> Use working methods
                minimising dust; vacuum at source. Asbestos insulation or coating
                must only be handled by licensed specialists under the HSC Code of
                Practice.
              </li>
              <li>
                <strong>Flammable &amp; toxic vapours:</strong> Electrical equipment
                in hazardous areas must comply with BS 5345. Provide mechanical
                ventilation to keep vapour concentrations below toxic thresholds
                and ensure exhaust air is isolated from occupied building intake
                ducts.
              </li>
              <li>
                <strong>Temporary electrical installations:</strong> Portable
                tools, temporary distribution panels, and site supplies must
                strictly comply with BS 4363 and the IEE Wiring Regulations. In
                confined metal enclosures or boilers, use 50V (25V to earth) or
                25V (12.5V to earth) extra-low voltage systems.
              </li>
              <li>
                <strong>Noise control:</strong> Select low-noise hydraulic or
                electrically driven tools rather than pneumatic where possible;
                erect acoustic screening and ensure operative hearing protection
                in accordance with BS 5228.
              </li>
              <li>
                <strong>Protection of openings:</strong> Floor penetrations,
                cable trenches, manholes, and riser access openings must be
                continuously guarded with rigid barriers, toe boards, and warning
                lights.
              </li>
              <li>
                <strong>Inspection &amp; competent persons:</strong> Regular
                statutory examinations of electrical machinery and distribution
                must be conducted by technically competent, certified inspectors.
              </li>
            </ul>

            <h3 className={styles.subheading}>Statutory legislation &amp; guidance</h3>
            <p className={styles.bodyParagraph}>
              The electrical and building services installations are governed by
              British Standards (referenced through BSI and the OPUS Design File)
              and UK Health and Safety Executive legislation:
            </p>

            <div className={styles.tagGrid}>
              {LEGISLATION_LIST.map((law) => (
                <span key={law} className={styles.legislationTag}>
                  {law}
                </span>
              ))}
            </div>
          </article>

          <footer className={styles.pageFooter}>
            End of current page content ·{" "}
            <a href="#health-safety-electrical">Back to top</a>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default withPage(HealthSafetyElectrical)({
  meta: {
    title: "Health & safety electrical | Commerzbank | Pulse",
    description:
      "Statutory health and safety information, method statements, risk assessments, and electrical safe working procedures for Commerzbank building systems.",
  },
  breadcrumbs: [
    { label: clientContent.systemName, href: "/" },
    { label: clientContent.clientName },
    { label: "Health & safety", href: "/more-info" },
    { label: "Electrical" },
  ],
  searchPlaceholder: "Search all information",
});
